import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { api_routes, db_tables } from '@app/consts';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ActivatedRoute } from '@angular/router';
import { TablePermissionsService } from '@app/core/services/table-permissions/table-permissions.service';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { EmployeeProjectsService } from './services/employee-projects.service';
import { EmployeeProject, EmployeeProjectVerbose } from './models/employee-projects.model';

@Component({
  selector: 'app-employee-projects',
  templateUrl: './employee-projects.component.html',
  styleUrls: ['./employee-projects.component.scss']
})
export class EmployeeProjectsComponent implements OnInit {
  @Input() formIds: string[];
  @Input() employeeID: string;

  public columns: any[] = [
    { field: "projectName", title: "project Name", tableId: "tfi_ProjectName"},
    { field: "projectJobTitle", title: "project Job Title", tableId: "tfi_ProjectJobTitle"},
    { field: "startDate", title: "start Date", tableId: "tfi_ProjStartDate", type: "date"},
    { field: "endDate", title: "end Date", tableId: "tfi_ProjEndDate", type: "date"},
    { field: "projectLeader", title: "project Leader", tableId: "tfi_ProjectLeader"},
    { field: "projectSize", subField: "text", title: "project Size", tableId: "tfi_ProjectSize"},
    { field: "comments", title: "comments", tableId: "tfi_ProjectComments"},
  ];
  public bindingType: String = "array";
  public view: any;
  public gridData: any;
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 5;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: MatDialogRef<FormGeneratorDialogComponent>;
  formId: string = 'frm_Q9eF8wzK4QFzRK'
  changeReasonFormId: string = 'frm_S3q7rXXVRiNv9p';
  formValid: boolean;
  private sortString: string = '';
  isLoadingPermissions: boolean = true;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  accessAndHistoryURL: string = `${api_routes.TALENT}/[employeeId]/${api_routes.PROJECTS}/[entityId]`;

  constructor(
    private dialog: MatDialog,
    public translate: TranslateService,
    private employeeProjectService: EmployeeProjectsService,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private tablePermissionsService: TablePermissionsService,
    private route: ActivatedRoute,
  ) {
    this.route.paramMap.subscribe(
        params => this.employeeID = params.get("id")
    )
  }

  ngOnInit(): void {
    this.getTargetEmployeeTablePermissions();
    this.view = this.getProjects();
  }

  getTargetEmployeeTablePermissions() {
    this.isLoadingPermissions = true;

    let currentFetchedData = this.tablePermissionsService.getFetchedTargetEmployeeTablePermissions(this.employeeID);

    // if the current table permissions that are saved are for this employee use those, else fetch the new permissions.
    if(currentFetchedData !== null){
      this.targetEmployeeTablePermissions = currentFetchedData;
      this.isLoadingPermissions = false;
    }
    else {
      this.tablePermissionsService.getTargetEmployeeTablePermissions(this.employeeID)
      .pipe(
        finalize(() => {
            this.isLoadingPermissions = false;
        })
      )
      .subscribe(
        res => {
          this.targetEmployeeTablePermissions = res;
        }
      );
    }
  }

  getProjects() {
    this.isLoading = true;

    this.employeeProjectService.getEmployeeProjects(this.employeeID, this.pageSize, this.skip, this.sortString)
      .pipe(
        finalize( () => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }
          this.gridData = res.data;
        }
      );
  }

  getProject(professionalExpertise?: EmployeeProject) {
    if(professionalExpertise === undefined){
      this.openFormDialog();
    }
    else {
      this.isLoading = true;

      this.employeeProjectService.getEmployeeProject(this.employeeID, professionalExpertise.id)
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe(
        res => {
          this.openFormDialog(res);
        }
      );
    }
  }

  openFormDialog(professionalExpertise?: EmployeeProjectVerbose) {
    let formData = {
      id: professionalExpertise ? professionalExpertise.id : null,
      asOf: moment().format(),
      changeReason: '',
      changeReasonComments: '',
      projectName: professionalExpertise ? professionalExpertise.projectName : null,
      projectJobTitle: professionalExpertise ? professionalExpertise.projectJobTitle : null,
      startDate: professionalExpertise ? professionalExpertise.startDate : null,
      endDate: professionalExpertise ? professionalExpertise.endDate : null,
      projectLeader: professionalExpertise ? professionalExpertise.projectLeader : null,
      projectSize: professionalExpertise ? professionalExpertise.projectSize?.id : null,
      comments: professionalExpertise ? professionalExpertise.comments : null,
    };

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('Project')}`,
      formId: this.formId,
      changeReasonFormId: this.changeReasonFormId,
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.save(event)
    });
  }

  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData: any){
    this.overlayService.show();

    if(!formData.id){
      this.employeeProjectService.createEmployeeProject(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getProjects();
        }
      );
    }
    else {
      this.employeeProjectService.updateEmployeeProject(this.employeeID, formData)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
          this.dialogRef.close(true);
          this.getProjects();
        }
      );
    }
  }

  deleteProjects(professionalExpertiseIds: string[]) {
    const observables = professionalExpertiseIds.map(selectedItem => defer(() => this.employeeProjectService.deleteEmployeeProject(this.employeeID, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.view = this.getProjects();
        this.clearSelectedItems = !this.clearSelectedItems;
          this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
      }
    );
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.view = this.getProjects();
  }

}




